// SaveRouteGate — оборачивает все /saves/:saveId/* маршруты:
//   - читает :saveId из URL
//   - подтягивает World snapshot
//   - на 404/403 — единый "не найден или нет доступа"
//   - кладёт save+world+myRole в WorldContext (доступно через useSave/useWorld/etc.)

import React from 'react';
import { Outlet, useNavigate, useParams, Link } from 'react-router-dom';
import { Alert, Breadcrumb, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetSaveWorldQuery } from '../api/savesV2Api';
import { WorldProvider, type WorldContextValue } from '../contexts/WorldContext';
import MainLayout from '../components/common/MainLayout';
import type { SaveWithProjection, SaveWorld } from '../types/saves';

const buildSaveFromWorld = (world: SaveWorld): SaveWithProjection => ({
  id: world.saveId,
  userId: '',
  type: world.type,
  status: world.status,
  simSessionId: null,
  revision: world.revision,
  engineVersion: world.engineVersion,
  cursor: world.cursor,
  cachePolicy: 'none',
  scenarioTemplateId: world.scenario?.templateId ?? null,
  scenarioTemplateVersion: world.scenario?.templateVersion ?? null,
  farmId: world.farm.id,
  farmName: world.farm.name,
  capabilitiesSnapshot: world.capabilities,
  weatherMapId: world.runtime?.config?.weatherSeriesId ?? null,
  measurementSystem: world.measurementSystem ?? null,
  unitPreferences: world.unitPreferences ?? null,
  planScale: world.planScale ?? null,
  createdAt: '',
  updatedAt: '',
  lastPlayedAt: null,
  completedAt: world.runtime?.terminal ? world.cursor?.simDate ?? null : null,
});

const Loader: React.FC = () => {
  const { t } = useTranslation('layout');
  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('breadcrumbs.home')}</Link> },
            { title: <Link to="/saves">{t('breadcrumbs.saves')}</Link> },
          ]}
        />
      }
    >
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    </MainLayout>
  );
};

const NotFoundOrForbidden: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation('layout');
  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('breadcrumbs.home')}</Link> },
            { title: <Link to="/saves">{t('breadcrumbs.saves')}</Link> },
            { title: t('breadcrumbs.save') },
          ]}
        />
      }
    >
      <Alert
        type="error"
        showIcon
        message={t('saveGate.notFoundTitle')}
        description={message || t('saveGate.notFoundDescription')}
      />
    </MainLayout>
  );
};

const SaveRouteGate: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('layout');

  const {
    data: world,
    isLoading: worldLoading,
    isError: worldError,
    refetch: refetchWorld,
  } = useGetSaveWorldQuery(saveId || '', { skip: !saveId });

  if (!saveId) {
    return <NotFoundOrForbidden message={t('saveGate.missingSaveId')} />;
  }

  if (worldLoading) {
    return <Loader />;
  }

  if (worldError || !world) {
    return <NotFoundOrForbidden />;
  }

  const save = buildSaveFromWorld(world);
  const value: WorldContextValue = {
    save,
    world,
    myRole: world.farm.myRole,
    refresh: async () => {
      await refetchWorld();
    },
  };

  // Suppress unused-variable warning for navigate (intended for future redirects).
  void navigate;

  return (
    <WorldProvider value={value}>
      <Outlet />
    </WorldProvider>
  );
};

export default SaveRouteGate;
